import { createContext, useContext, useEffect, useState } from "react";
import { Jazz, Telenor, Ufone, Zong } from "../assets/images";
import { useSnackbar } from "./SnackbarContext";
import { useAuthContext } from "./AuthContext";
import { handleApi } from "../apis";
import {
  GET_ACCOUNT_INFO,
  GET_BENEFICIARY_LIST,
  GET_ACCOUNT_LIMIT,
  GET_GENERAL_WALLET_INFO,
  GET_POPULAR_BILLS,
  GET_POPULAR_VOUCHERS,
  GET_WALLET_BALANCE,
  GETBANKLIST,
  GETBILLLIST,
  GETVOCHURELIST,
  GETWALLETTRANSACTION,
  SEARCH_BILL_CUSTOMER,
  WALLET_GERNERAL_V6,
  GET_TRANSACTION_LIST_V2,
  GET_EMI_STATUS,
} from "../apis/configuration";
import { EMIONBOARDINGSTATUS, VERIFIED } from "../constants";

export const WalletContext = createContext({
  banks: [],
  setBanks: () => {},
  fetchWalletData: () => {},
  walletDataIsLoading: false,
  earnMoney: {
    easyloadPackages: { list: [], commission: 0 },
    billPayments: { list: [], commission: 0 },
    vouchers: { list: [], commission: 0 },
  },
  latitude: null,
  longitude: null,
  walletData: {},
  transactionIsLoading: false,
  moneyIn: 0,
  moneyOut: 0,
  transactions: [],
  setWalletData: () => {},
  fetchBankList: () => {},
  bankIsLoading: false,
  billIsLoading: false,
  fetchBillList: () => {},
  vochureIsLoading: false,
  fetchVochureList: () => {},
  fetchAccountInfo: () => {},
  isAccountLoading: false,
  accountInfo: null,
  KYCVerified: false,
  setKYCVerified: () => {},
  fetchBeneficiaryList: () => {},
  isBeneficiaryListLoading: false,
  beneficiaryList: [],
  handleGetWalletBalance: () => {},
  fetchAccountBalanceLoading: false,
  handleFetchPopularVouchers: () => {},
  popularVouchers: [],
  popularVouchersLoading: false,
  loadingAccountLimit: false,
  accountLimit: null,
  getAccountLimit: () => {},
  txnFilterOptionsCache: {},
  cacheTxnFilterOptions: () => {},
});
const netWorkIcon = { Telenor: Telenor, Zong: Zong, Ufone: Ufone, Jazz: Jazz };

export const WalletProvider = ({ children }) => {
  const [walletData, setWalletData] = useState();
  const [banks, setBanks] = useState([]);
  const [walletDataIsLoading, setWalletDataIsLoading] = useState(false);
  const [transactionIsLoading, setTransactionIsLoading] = useState(false);
  const [bankIsLoading, setBankIsLoading] = useState(false);
  const [billIsLoading, setBillIsLoading] = useState(false);
  const [vochureIsLoading, setVochureIsLoading] = useState(false);
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const [accountInfo, setAccountInfo] = useState();
  const [KYCVerified, setKYCVerified] = useState(false);
  const [EMIVerified, setEMIVerified] = useState(false);
  const [beneficiaryList, setBeneficiaryList] = useState([]);
  const [popularBills, setPopularBills] = useState([]);
  const [popularVouchers, setPopularVouchers] = useState([]);
  const [popularBillsLoading, setPopularBillsLoading] = useState(false);
  const [popularVouchersLoading, setPopularVouchersLoading] = useState(false);
  const [isBeneficiaryListLoading, setIsBeneficiaryListLoading] =
    useState(false);
  const [fetchAccountBalanceLoading, setFetchAccountBalanceLoading] =
    useState(false);

  const [earnMoney, setEarnMoney] = useState({
    easyloadPackages: { list: [], commission: 0 },
    billPayments: { list: [], commission: 0 },
    allBillPayments: { list: [], commission: 0 },
    vouchers: { list: [], commission: 0 },
    allVouchers: { list: [], commission: 0 },
  });
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [moneyIn, setMoneyIn] = useState(0);
  const [moneyOut, setMoneyOut] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [accountLimit, setAccountLimit] = useState();
  const [loadingAccountLimit, setLoadingAccountLimit] = useState(false);
  // Company/Type transaction filter options, cached per txn-type group (e.g. recharge,
  // voucher_payment) so every listing/contact-detail page doesn't refetch the same options.
  const [txnFilterOptionsCache, setTxnFilterOptionsCache] = useState({});

  const cacheTxnFilterOptions = (key, partial) => {
    setTxnFilterOptionsCache((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...partial },
    }));
  };

  const { showSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  useEffect(() => {
    if (!user) {
      setWalletData(undefined);
      setBanks([]);
      setAccountInfo(undefined);
      setKYCVerified(false);
      setEMIVerified(false);
      setEarnMoney({
        easyloadPackages: { list: [], commission: 0 },
        billPayments: { list: [], commission: 0 },
        allBillPayments: { list: [], commission: 0 },
        vouchers: { list: [], commission: 0 },
        allVouchers: { list: [], commission: 0 },
      });
      setTransactions([]);
      setMoneyIn(0);
      setMoneyOut(0);
      setBeneficiaryList([]);
      setIsBeneficiaryListLoading(false);
    }
  }, [user]);

  const handleCheckEMIStatus = async (navigate) => {
    try {
      const response = await handleApi(GET_EMI_STATUS, null, null, navigate, {
        "Api-Key": process.env.REACT_APP_API_KEY,
      });
      setEMIVerified(!!response?.verified);
      return !!response?.verified;
    } catch (err) {
      setEMIVerified(false);
      // handleApi already shows a toast for network errors and for 401/403
      // (inactive/locked/relogin) — only surface a message here for the
      // remaining cases (e.g. 4xx/5xx from the EMI status endpoint itself),
      // so the user isn't silently redirected to onboarding with no explanation.
      const alreadyToasted =
        err?.message === "Network Error" ||
        err?.code === "ERR_NETWORK" ||
        !err?.response ||
        err?.response?.status === 401 ||
        err?.response?.status === 403;
      if (!alreadyToasted) {
        showSnackbar({
          message:
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            "Unable to verify your account status. Please try again.",
          severity: "error",
          icon: "",
        });
      }
      throw err;
    }
  };

  const getAccountLimit = async (navigate) => {
    try {
      setLoadingAccountLimit(true);
      const accountLimit = await handleApi(
        GET_ACCOUNT_LIMIT,
        null,
        null,
        navigate,
      );
      if (accountLimit?.success) {
        const response =
          accountLimit?.data?.length > 0 ? accountLimit?.data[0] : null;
        setAccountLimit(response);
        setLoadingAccountLimit(false);
      } else setLoadingAccountLimit(false);
    } catch (error) {
      setLoadingAccountLimit(false);
    }
  };

  const fetchTransactionList = async (navigate) => {
    setTransactionIsLoading(true);
    try {
      let params = {
        page: 1,
        limit: 7,
      };
      const data = await handleApi(
        GET_TRANSACTION_LIST_V2,
        params,
        null,
        navigate,
      );
      if (data?.success) {
        setTransactions(data?.transactions);
        setMoneyIn(data?.money_in);
        setMoneyOut(data?.money_out);
        setTransactionIsLoading(false);
      } else {
        setTransactionIsLoading(false);
      }
    } catch (error) {
      setTransactionIsLoading(false);
      if (error.message === "New request appears, canceling previous request.")
        return;
      showSnackbar({
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Something went wrong while fetching transactions",
        severity: "error",
        icon: "",
      });
    }
  };

  const fetchWalletData = async ({ navigate } = {}) => {
    if (!EMIVerified) {
      setWalletDataIsLoading(false);
      return;
    }
    try {
      setWalletDataIsLoading(true);
      let data = await handleApi(WALLET_GERNERAL_V6, null, null, navigate);
      let easyloadPackages = [];
      let billPayments = [];
      let vouchers = [];
      setWalletData(data);
      const isKYCVerified = data?.account_info?.verified === VERIFIED.VERIFIED;
      setKYCVerified(isKYCVerified);
      if (isKYCVerified) {
        if (
          data?.account_info?.emi_onboarding_status ===
          EMIONBOARDINGSTATUS.VERIFIED
        ) {
          if (!(beneficiaryList.length > 0)) fetchBeneficiaryList(navigate);
          fetchTransactionList(navigate);
          getAccountLimit(navigate);
        }
        setBanks(
          data?.banks.map((item) => {
            return {
              display_name: item?.display_name,
              image: item?.logo,
              name: item?.name,
            };
          }),
        );
        easyloadPackages = data?.available_telcos.map((item) => {
          return {
            name: item.name,
            logo: netWorkIcon[item.name],
            active: item.active,
          };
        });
        const popularBillsFromV6 = data?.popular_bills || [];
        if (popularBillsFromV6.length > 0) setPopularBills(popularBillsFromV6);
        billPayments = popularBillsFromV6.length
          ? popularBillsFromV6.map((item) => ({
              name: item?.name,
              logo: item?.icon,
            }))
          : (data?.available_billers || []).map((item) => ({
              name: item?.business,
              logo: item?.business_icon,
            }));
        vouchers = (data?.available_vouchers || []).map((item) => {
          return { name: item?.vendor_name, logo: item?.icon };
        });
        setEarnMoney((e) => ({
          ...e,
          easyloadPackages: { list: easyloadPackages },
          billPayments: { list: billPayments },
          vouchers: { list: vouchers },
        }));
      }
      setWalletDataIsLoading(false);
    } catch (error) {
      showSnackbar({
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed fetch Wallet data",
        severity: "error",
        icon: "",
      });
      setWalletDataIsLoading(false);
    }
  };
  const fetchBankList = async (navigate) => {
    if (banks.length > 0) return;
    try {
      setBankIsLoading(true);
      const data = await handleApi(GETBANKLIST, null, null, navigate);
      setBanks(
        data?.banks.map((item) => {
          return {
            display_name: item?.display_name,
            image: item?.logo,
            name: item?.name,
          };
        }),
      );
      setBankIsLoading(false);
    } catch (error) {
      setBankIsLoading(false);
    }
  };

  const fetchBillList = async (navigate) => {
    let allBillPayments = [];
    try {
      setBillIsLoading(true);
      const data = await handleApi(GETBILLLIST, null, null, navigate);
      setWalletData((w) => ({ ...w, available_billers: data }));
      allBillPayments = data?.map((item) => {
        return { name: item?.business, logo: item?.business_icon };
      });

      setEarnMoney((e) => ({
        ...e,
        allBillPayments: { list: allBillPayments },
      }));
      setBillIsLoading(false);
    } catch (error) {
      setBillIsLoading(false);
    }
  };

  const fetchVochureList = async (navigate) => {
    let allVouchers = [];
    try {
      setVochureIsLoading(true);
      const data = await handleApi(GETVOCHURELIST, null, null, navigate);
      allVouchers = data.map((item) => {
        return { name: item?.vendor_name, logo: item?.icon };
      });
      setEarnMoney((e) => ({
        ...e,
        allVouchers: { list: allVouchers },
      }));
      setWalletData((w) => ({ ...w, available_vouchers: [...data] }));
      setVochureIsLoading(false);
      return data;
    } catch (error) {
      setVochureIsLoading(false);
      return [];
    }
  };

  const fetchAccountInfo = async (navigate) => {
    try {
      setIsAccountLoading(true);
      const response = await handleApi(
        GET_ACCOUNT_INFO,
        null,
        null,
        navigate,
        null,
        null,
        "/QuickSale/QuickSaleForm",
      );

      if (response?.success) {
        setAccountInfo(response.account_info);
        setIsAccountLoading(false);
        return response.account_info;
      }
    } catch (error) {
      setIsAccountLoading(false);
    } finally {
      setIsAccountLoading(false);
    }
  };

  const fetchBeneficiaryList = async (navigate) => {
    try {
      setIsBeneficiaryListLoading(true);

      let response = await handleApi(
        GET_BENEFICIARY_LIST,
        null,
        null,
        navigate,
      );

      let { accounts } = response || {};

      setBeneficiaryList(accounts || []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsBeneficiaryListLoading(false);
    }
  };

  const handleGetWalletBalance = async (navigate) => {
    try {
      setFetchAccountBalanceLoading(true);

      let response = await handleApi(GET_WALLET_BALANCE, null, null, navigate);
      let { current_balance } = response || {};

      setWalletData((w) => ({ ...w, current_balance }));
    } catch (error) {
      console.error(error);
    } finally {
      setFetchAccountBalanceLoading(false);
    }
  };

  const handleFetchPopularBills = async (
    navigate,
    params = {
      limit: 3,
      offset: 0,
    },
  ) => {
    try {
      setPopularBillsLoading(true);
      const response = await handleApi(
        GET_POPULAR_BILLS,
        params,
        null,
        navigate,
      );
      const { popular_bills } = response;
      setPopularBills(popular_bills || []);
    } catch (error) {
      console.error(error);
    } finally {
      setPopularBillsLoading(false);
    }
  };

  const handleSearchCustomerBill = async ({
    consumer_number,
    billing_company,
    cancelToken,
    navigate,
  }) => {
    const response = await handleApi(
      SEARCH_BILL_CUSTOMER,
      { consumer_number, company: billing_company },
      cancelToken,
      navigate,
    );
    return response;
  };

  const handleFetchPopularVouchers = async (
    navigate,
    params = {
      limit: 3,
      offset: 0,
    },
  ) => {
    try {
      setPopularVouchersLoading(true);
      const response = await handleApi(
        GET_POPULAR_VOUCHERS,
        params,
        null,
        navigate,
      );
      const { popular_vouchers } = response;
      setPopularVouchers(popular_vouchers || []);
    } catch (error) {
      console.error(error);
    } finally {
      setPopularVouchersLoading(false);
    }
  };

  return (
    <WalletContext.Provider
      value={{
        banks,
        setBanks,
        fetchWalletData,
        walletDataIsLoading,
        earnMoney,
        latitude,
        longitude,
        walletData,
        transactionIsLoading,
        moneyIn,
        moneyOut,
        transactions,
        setWalletData,
        fetchBankList,
        bankIsLoading,
        billIsLoading,
        fetchBillList,
        vochureIsLoading,
        fetchVochureList,
        fetchAccountInfo,
        isAccountLoading,
        accountInfo,
        KYCVerified,
        setKYCVerified,
        EMIVerified,
        setEMIVerified,
        handleCheckEMIStatus,
        beneficiaryList,
        fetchBeneficiaryList,
        isBeneficiaryListLoading,
        handleGetWalletBalance,
        fetchAccountBalanceLoading,
        loadingAccountLimit,
        accountLimit,
        handleFetchPopularBills,
        popularBills,
        popularBillsLoading,
        handleSearchCustomerBill,
        handleFetchPopularVouchers,
        popularVouchers,
        popularVouchersLoading,
        getAccountLimit,
        txnFilterOptionsCache,
        cacheTxnFilterOptions,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWalletContext = () => useContext(WalletContext);
